import React, { useState, useEffect } from"react";
import { useNavigate, useSearchParams } from"react-router-dom";
import { useTranslation } from"react-i18next";
import { Filter, CheckCircle, Clock, XCircle } from"lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Toast,
 ToastType,
 ModernButton,
 ModernSelect,
 ListPageSkeleton,
 Tabs,
 TabsList,
 TabsTrigger,
 TabsContent,
} from"../../components/ui";
import { apiService } from"../../services/api";
import { Sale } from"../../types";
import { SalesListView } from"../../components/financial/SalesListView";
import { DatePicker } from"../../components/form/DatePicker";
import { SalesPipelineTabContent } from"./SalesPipelineTabContent";

function localDayStartMs(yyyyMmDd: string): number {
 const [y, m, d] = yyyyMmDd.split("-").map(Number);
 return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

function localDayEndMs(yyyyMmDd: string): number {
 const [y, m, d] = yyyyMmDd.split("-").map(Number);
 return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

function saleInstantMs(saleDate: string): number {
 return new Date(saleDate).getTime();
}

export const SalesPage: React.FC = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const [searchParams, setSearchParams] = useSearchParams();
 const salesTab = searchParams.get("tab") === "pipeline" ? "pipeline" : "list";
 const [sales, setSales] = useState<Sale[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    status:"all"as"all"|"pending"|"confirmed"|"cancelled",
    salespersonId:"",
    startDate: "",
    endDate: "",
  });

 useEffect(() => {
 fetchSales();
 }, [page, filters]);

 const fetchSales = async () => {
 try {
 setIsLoading(true);
 const response = await apiService.getSales({ page, limit: 50 });
 let filteredSales = response.sales || [];

 if (filters.status !=="all") {
 filteredSales = filteredSales.filter(
 (sale: Sale) => sale.status === filters.status
 );
 }
 if (filters.salespersonId) {
 filteredSales = filteredSales.filter(
 (sale: Sale) => sale.salespersonId === filters.salespersonId
 );
 }
 if (filters.startDate) {
 const startMs = localDayStartMs(filters.startDate);
 filteredSales = filteredSales.filter(
 (sale: Sale) => saleInstantMs(sale.saleDate) >= startMs
 );
 }
 if (filters.endDate) {
 const endMs = localDayEndMs(filters.endDate);
 filteredSales = filteredSales.filter(
 (sale: Sale) => saleInstantMs(sale.saleDate) <= endMs
 );
 }

 setSales(filteredSales);
 setTotalPages(response.pagination?.totalPages || 1);
 } catch (err) {
 console.error("Erro ao carregar vendas:", err);
 setToast({
 message: t("financialSales.loadError"),
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const handleViewDetails = (saleId: string) => {
 navigate(`/financial/sales/${saleId}`);
 };

 const getStatusIcon = (status: Sale["status"]) => {
 switch (status) {
 case"confirmed":
 case"paid":
 case"delivered":
 return <CheckCircle className="h-5 w-5 text-green-600"/>;
 case"pending":
 case"open":
 case"waiting_payment":
 return <Clock className="h-5 w-5 text-yellow-600"/>;
 case"in_preparation":
 case"ready_for_delivery":
 return <Clock className="h-5 w-5 text-blue-600"/>;
 case"cancelled":
 return <XCircle className="h-5 w-5 text-red-600"/>;
 default:
 return <Clock className="h-5 w-5 text-muted-foreground"/>;
 }
 };

 const getStatusLabel = (status: Sale["status"]) =>
 t(`financialSales.statusLabels.${status}`, { defaultValue: status });

 return (
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <h1 className="text-2xl font-bold text-foreground font-display">
 {t("financialSales.title")}
 </h1>
 </div>

 <Tabs
 value={salesTab}
 onValueChange={(v) => {
 if (v === "pipeline") {
 setSearchParams({ tab: "pipeline" });
 } else {
 setSearchParams({});
 }
 }}
 className="w-full"
 >
 <TabsList className="grid w-full max-w-md grid-cols-2">
 <TabsTrigger value="list">{t("financialSales.tabs.list")}</TabsTrigger>
 <TabsTrigger value="pipeline">{t("financialSales.tabs.pipeline")}</TabsTrigger>
 </TabsList>

 <TabsContent value="list" className="space-y-6 mt-4">
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <Filter className="h-5 w-5 mr-2"/>
 {t("financialSales.filters")}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <ModernSelect
 label={t("financialSales.status")}
 value={filters.status}
 onChange={(e) =>
 setFilters({
 ...filters,
 status: e.target.value as typeof filters.status,
 })
 }
 options={[
 { value: "all", label: t("financialSales.statusAll") },
 { value: "open", label: t("financialSales.statusLabels.open") },
 { value: "pending", label: t("financialSales.statusLabels.pending") },
 { value: "waiting_payment", label: t("financialSales.statusLabels.waiting_payment") },
 { value: "paid", label: t("financialSales.statusLabels.paid") },
 { value: "in_preparation", label: t("financialSales.statusLabels.in_preparation") },
 { value: "ready_for_delivery", label: t("financialSales.statusLabels.ready_for_delivery") },
 { value: "delivered", label: t("financialSales.statusLabels.delivered") },
 { value: "confirmed", label: t("financialSales.statusLabels.confirmed") },
 { value: "cancelled", label: t("financialSales.statusLabels.cancelled") },
 ]}
 placeholder=""
 />
 <div>
 <DatePicker
 label={t("financialSales.startDate")}
 value={filters.startDate}
 onChange={(value) =>
 setFilters({ ...filters, startDate: value })
 }
 placeholder={t("financialSales.startDatePlaceholder")}
 />
 </div>
 <div>
 <DatePicker
 label={t("financialSales.endDate")}
 value={filters.endDate}
 onChange={(value) => setFilters({ ...filters, endDate: value })}
 placeholder={t("financialSales.endDatePlaceholder")}
 />
 </div>
 <div className="flex items-end">
 <ModernButton
 variant="outline"
 onClick={() =>
 setFilters({
 status:"all",
 salespersonId:"",
 startDate:"",
 endDate:"",
 })
 }
 className="w-full"
 >
 {t("financialSales.clearFilters")}
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>

 {isLoading && sales.length === 0 ? (
 <ListPageSkeleton rows={6} showFilters={false} />
 ) : (
 <SalesListView
 sales={sales}
 isLoading={isLoading}
 onViewDetails={handleViewDetails}
 getStatusIcon={getStatusIcon}
 getStatusLabel={getStatusLabel}
 page={page}
 totalPages={totalPages}
 onPageChange={setPage}
 />
 )}
 </TabsContent>

 <TabsContent value="pipeline" className="mt-4">
 <SalesPipelineTabContent active={salesTab === "pipeline"} />
 </TabsContent>
 </Tabs>

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 />
 )}
 </div>
 );
};
